import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAuth } from '../hooks/useAuth.jsx';
import { rule } from '../lib/fieldRules.js';
import { Seo } from '../components/Seo.jsx';
import { Button, Field, Input, Alert } from '../components/ui.jsx';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [busy, setBusy] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    setFieldErrors({});
    try {
      await api.post('/auth/reset', { token, password });
      await refresh(); // The server set the session cookie; pick it up.
      navigate('/dashboard', { replace: true });
    } catch (err) {
      // A mangled link fails the server's token-shape check ("Validation failed"
      // on `token`); to the person it is the same dead link as an expired one.
      if (err.status === 400 && err.details && 'token' in err.details) {
        setError('That reset link is invalid or has expired. Request a new one.');
        setFieldErrors({});
      } else {
        setError(err.message);
        setFieldErrors(err.details ?? {});
      }
      setBusy(false);
    }
  }

  return (
    <>
      <Seo title="Choose a new password" noindex />
      <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-6 py-12">
        <Link to="/" className="text-2xs font-semibold uppercase tracking-widest text-accent">
          Affily
        </Link>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight">Choose a new password</h1>

        {!token ? (
          <Alert className="mt-6">
            This link is incomplete. <Link to="/forgot-password" className="font-medium underline">Request a new one</Link>.
          </Alert>
        ) : (
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <Alert>
              {error}{' '}
              {error && /invalid or has expired/i.test(error) && (
                <Link to="/forgot-password" className="font-medium underline">
                  Request a new link
                </Link>
              )}
            </Alert>
            <Field label="New password" required constraint={rule.password()} error={fieldErrors.password}>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                minLength={10}
                required
              />
            </Field>
            <p className="text-xs text-ink-muted">
              Changing your password signs out every other device.
            </p>
            <Button type="submit" size="lg" className="w-full" disabled={busy}>
              {busy ? 'Saving…' : 'Save password and sign in'}
            </Button>
          </form>
        )}
      </div>
    </>
  );
}
